import React, { useState, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API_ENDPOINTS from '../../api/apiConfig';
import { MOBILE_RE, resetPasswordWithOtp, sendOtp } from '../../api/otpService';
import OtpVerifier from '../../components/auth/OtpVerifier';
import AuthModal, {
  AlertIcon,
  ArrowLeftIcon,
  CheckIcon,
  EyeIcon,
  EyeOffIcon,
  Spinner,
} from '../../components/auth/AuthModal';
import shotMain from '../../assets/images/banner-image-2.jpg';
import shotSide from '../../assets/images/cat-large-item2.jpg';
import shotDot from '../../assets/images/banner-image-1.jpg';

const EMAIL_RE = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/;

const PANEL_PERKS = [
  'Track every order in one place',
  'Save favourites to your wishlist',
  'Check out faster with saved addresses',
];

// ─────────────────────────────────────────────────────────────
// Forgot Password — via Mobile OTP (Fast2SMS)
// ─────────────────────────────────────────────────────────────
interface ForgotPasswordProps {
  onClose: () => void;
  switchToLogin: () => void;
  initialMobile?: string;
  onUseEmail?: () => void;
}

type ResetStep = 'mobile' | 'otp' | 'password' | 'done';

const ForgotPasswordMobile = ({ onClose, switchToLogin, initialMobile = '', onUseEmail }: ForgotPasswordProps) => {
  const [step, setStep] = useState<ResetStep>('mobile');
  const [mobile, setMobile] = useState(initialMobile.replace(/\D/g, '').slice(-10));
  const [mobileError, setMobileError] = useState('');
  const [sending, setSending] = useState(false);
  const [resendIn, setResendIn] = useState(60);
  const [otpToken, setOtpToken] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSendOtp = async () => {
    const m = mobile.replace(/\D/g, '');
    if (!MOBILE_RE.test(m)) { setMobileError('Enter a valid 10-digit mobile number'); return; }
    setMobileError(''); setSending(true);
    const res = await sendOtp(m, 'RESET');
    setSending(false);
    if (res.ok) {
      setMobile(m);
      setResendIn(res.data.resend_in ?? 60);
      setStep('otp');
    } else {
      setMobileError(res.message);
    }
  };

  const handleReset = async () => {
    if (pw.length < 6) { setPwError('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { setPwError('Passwords do not match'); return; }
    setPwError(''); setSaving(true);
    const res = await resetPasswordWithOtp(mobile, otpToken, pw);
    setSaving(false);
    if (res.ok) {
      setStep('done');
      toast.success(res.message, { position: 'top-center', autoClose: 2500 });
    } else if (res.data.code_key === 'OTP_TOKEN_INVALID') {
      setOtpToken(''); setStep('mobile');
      setMobileError(res.message);
    } else {
      setPwError(res.message);
    }
  };

  if (step === 'otp') {
    return (
      <OtpVerifier
        mobile={mobile}
        purpose="RESET"
        initialResendIn={resendIn}
        onVerified={(token) => { setOtpToken(token); setStep('password'); }}
        onBack={() => setStep('mobile')}
      />
    );
  }

  if (step === 'done') {
    return (
      <div className="ffa-success">
        <div className="ffa-success-icon"><CheckIcon /></div>
        <p className="ffa-eyebrow">All set</p>
        <h2 id="ffa-login-title" className="ffa-title">Password <em>updated</em></h2>
        <p className="ffa-subtitle">You can now sign in with your new password.</p>
        <button type="button" className="ffa-btn" onClick={switchToLogin}>Back to login</button>
      </div>
    );
  }

  if (step === 'password') {
    return (
      <>
        <p className="ffa-eyebrow">Mobile verified</p>
        <h2 id="ffa-login-title" className="ffa-title">Set a new <em>password</em></h2>
        <p className="ffa-subtitle">For account +91 {mobile}</p>

        {pwError && (
          <div className="ffa-alert" role="alert">
            <AlertIcon />
            <span>{pwError}</span>
          </div>
        )}

        <div className="ffa-field">
          <label className="ffa-label" htmlFor="ffa-reset-pw">New password</label>
          <div className="ffa-input-wrap">
            <input
              id="ffa-reset-pw"
              className="ffa-input"
              type={showPw ? 'text' : 'password'}
              placeholder="Minimum 6 characters"
              value={pw}
              autoComplete="new-password"
              onChange={(e) => { setPw(e.target.value); setPwError(''); }}
            />
            <button
              type="button"
              className="ffa-eye"
              onClick={() => setShowPw((p) => !p)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPw ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        <div className="ffa-field">
          <label className="ffa-label" htmlFor="ffa-reset-pw2">Confirm password</label>
          <input
            id="ffa-reset-pw2"
            className="ffa-input"
            type={showPw ? 'text' : 'password'}
            placeholder="Re-enter new password"
            value={pw2}
            autoComplete="new-password"
            onChange={(e) => { setPw2(e.target.value); setPwError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleReset()}
          />
        </div>

        <button type="button" className="ffa-btn" onClick={handleReset} disabled={saving}>
          {saving ? <><Spinner /> Saving…</> : 'Update password'}
        </button>
      </>
    );
  }

  // step === 'mobile'
  return (
    <>
      <button type="button" className="ffa-back" onClick={switchToLogin}>
        <ArrowLeftIcon /> Back to login
      </button>

      <p className="ffa-eyebrow">Account recovery</p>
      <h2 id="ffa-login-title" className="ffa-title">Reset your <em>password</em></h2>
      <p className="ffa-subtitle">Enter your registered mobile number. We'll send you an OTP by SMS.</p>

      <div className="ffa-field">
        <label className="ffa-label" htmlFor="ffa-forgot-mobile">Mobile number</label>
        <input
          id="ffa-forgot-mobile"
          className={`ffa-input${mobileError ? ' ffa-input--error' : ''}`}
          type="tel"
          inputMode="numeric"
          maxLength={10}
          placeholder="10-digit mobile number"
          value={mobile}
          autoComplete="tel"
          onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '')); setMobileError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
        />
        {mobileError && <div className="ffa-field-error" role="alert">{mobileError}</div>}
      </div>

      <button type="button" className="ffa-btn" onClick={handleSendOtp} disabled={sending}>
        {sending ? <><Spinner /> Sending OTP…</> : 'Send OTP'}
      </button>
      <button type="button" className="ffa-btn ffa-btn--ghost" onClick={onClose} disabled={sending}>
        Close
      </button>

      {onUseEmail && (
        <p className="ffa-footer">
          No access to this number?{' '}
          <button type="button" className="ffa-link" onClick={onUseEmail}>Reset via email</button>
        </p>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Forgot Password — via Email link (fallback)
// ─────────────────────────────────────────────────────────────
const ForgotPasswordEmail = ({ onClose, switchToLogin }: ForgotPasswordProps) => {
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validate = (v: string): string => {
    if (!v.trim()) return 'Email address is required';
    if (!EMAIL_RE.test(v.trim())) return 'Enter a valid email address';
    return '';
  };

  const handleSend = async () => {
    const err = validate(email);
    if (err) { setEmailError(err); return; }
    setEmailError('');
    setIsSending(true);
    try {
      const res = await fetch(API_ENDPOINTS.FORGOT_PASSWORD_SEND, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setEmailSent(true);
      } else {
        setEmailError(data.message || 'Failed to send reset link.');
      }
    } catch {
      setEmailError('An error occurred. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (emailSent) {
    return (
      <div className="ffa-success">
        <div className="ffa-success-icon"><CheckIcon /></div>
        <p className="ffa-eyebrow">Email sent</p>
        <h2 id="ffa-login-title" className="ffa-title">Check your <em>inbox</em></h2>
        <p className="ffa-subtitle" style={{ marginBottom: 12 }}>We sent a password reset link to</p>
        <div className="ffa-email-pill">{email.trim()}</div>

        <div className="ffa-steps">
          <div className="ffa-steps-title">What to do next</div>
          {[
            'Open your email inbox',
            'Find the email from Fashion Frame',
            'Tap "Click Here to Reset Password"',
            'Enter your new password on the page that opens',
          ].map((s, i) => (
            <div className="ffa-step" key={s}>
              <span className="ffa-step-num">{i + 1}</span>
              <span>{s}</span>
            </div>
          ))}
        </div>

        <div className="ffa-expiry">This link expires in <strong>30 minutes</strong></div>

        <button type="button" className="ffa-btn" onClick={onClose}>Back to login</button>
        <p className="ffa-footer">
          Didn't receive it?{' '}
          <button type="button" className="ffa-link" onClick={() => setEmailSent(false)}>Try again</button>
        </p>
      </div>
    );
  }

  return (
    <>
      <button type="button" className="ffa-back" onClick={switchToLogin}>
        <ArrowLeftIcon /> Back to login
      </button>

      <p className="ffa-eyebrow">Account recovery</p>
      <h2 id="ffa-login-title" className="ffa-title">Reset your <em>password</em></h2>
      <p className="ffa-subtitle">Enter your registered email and we'll send you a secure reset link.</p>

      <div className="ffa-field">
        <label className="ffa-label" htmlFor="ffa-forgot-email">Email address</label>
        <input
          id="ffa-forgot-email"
          className={`ffa-input${emailError ? ' ffa-input--error' : ''}`}
          type="email"
          placeholder="you@example.com"
          value={email}
          autoComplete="email"
          onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        {emailError && <div className="ffa-field-error" role="alert">{emailError}</div>}
      </div>

      <button type="button" className="ffa-btn" onClick={handleSend} disabled={isSending}>
        {isSending ? <><Spinner /> Sending…</> : 'Send reset link'}
      </button>
      <button type="button" className="ffa-btn ffa-btn--ghost" onClick={onClose} disabled={isSending}>
        Close
      </button>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Login
// ─────────────────────────────────────────────────────────────
interface LoginProps {
  show?: boolean;
  onClose?: () => void;
  switchToRegistration?: () => void;
}

interface LoginFormData {
  contact_no: string;
  password: string;
  rememberMe: boolean;
}

type LoginView = 'login' | 'forgot' | 'forgot-email';

const Login = ({ show = false, onClose, switchToRegistration }: LoginProps) => {
  const navigate = useNavigate();
  const [view, setView] = useState<LoginView>('login');
  const [formData, setFormData] = useState<LoginFormData>({ contact_no: '', password: '', rememberMe: false });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show) { setView('login'); setError(''); }
  }, [show]);

  if (!show) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_no: formData.contact_no, password: formData.password }),
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('authToken', data.access_token);
        toast.success('Welcome back!', { position: 'top-center', autoClose: 2000 });
        setTimeout(() => { navigate('/'); window.location.reload(); if (onClose) onClose(); }, 2000);
      } else {
        setError(data.message || 'Invalid credentials. Please try again.');
        toast.error(data.message || 'Login failed.', { position: 'top-center', autoClose: 3000 });
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setView('login'); setError('');
    setFormData({ contact_no: '', password: '', rememberMe: false });
    if (onClose) onClose();
  };

  return (
    <AuthModal
      onClose={handleClose}
      gallery={[shotMain, shotSide, shotDot]}
      panelEyebrow="Member access"
      panelTitle={<>Dressed in <em>quiet</em> confidence.</>}
      perks={PANEL_PERKS}
      labelledBy="ffa-login-title"
    >
      {view === 'forgot' ? (
        <ForgotPasswordMobile
          onClose={handleClose}
          switchToLogin={() => setView('login')}
          initialMobile={formData.contact_no}
          onUseEmail={() => setView('forgot-email')}
        />
      ) : view === 'forgot-email' ? (
        <ForgotPasswordEmail onClose={handleClose} switchToLogin={() => setView('forgot')} />
      ) : (
        <>
          <p className="ffa-eyebrow">Member login</p>
          <h2 id="ffa-login-title" className="ffa-title">Welcome <em>back</em></h2>
          <p className="ffa-subtitle">Sign in to view your orders, wishlist and faster checkout.</p>

          {error && (
            <div className="ffa-alert" role="alert">
              <AlertIcon />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="ffa-field">
              <label className="ffa-label" htmlFor="ffa-login-contact">Contact number</label>
              <input
                id="ffa-login-contact"
                className="ffa-input"
                type="tel"
                inputMode="tel"
                name="contact_no"
                placeholder="Enter your contact number"
                value={formData.contact_no}
                onChange={handleChange}
                autoComplete="tel"
                required
              />
            </div>

            <div className="ffa-field">
              <label className="ffa-label" htmlFor="ffa-login-password">Password</label>
              <div className="ffa-input-wrap">
                <input
                  id="ffa-login-password"
                  className="ffa-input"
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="ffa-eye"
                  onClick={() => setShowPw((p) => !p)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="ffa-row">
              <label className="ffa-check">
                <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} />
                Remember me
              </label>
              <button type="button" className="ffa-link" onClick={() => { setView('forgot'); setError(''); }}>
                Forgot password?
              </button>
            </div>

            <button type="submit" className="ffa-btn" disabled={loading}>
              {loading ? <><Spinner /> Signing in…</> : 'Sign in'}
            </button>
          </form>

          <p className="ffa-footer">
            New to Fashion Frame?{' '}
            <button type="button" className="ffa-link" onClick={() => { if (switchToRegistration) switchToRegistration(); }}>
              Create an account
            </button>
          </p>
        </>
      )}
    </AuthModal>
  );
};

export default Login;